import React, { useEffect } from "react";
import {
  Drawer,
  Box,
  Stack,
  Typography,
  IconButton,
  Chip,
  CircularProgress,
  Paper,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { fetchAuditLog, clearCurrent } from "./auditLogSlice";

const SEVERITY_COLOR = {
  info: { bg: "#ccfbf1", fg: "#0f766e" },
  warning: { bg: "#fef3c7", fg: "#b45309" },
  critical: { bg: "#fee2e2", fg: "#b91c1c" },
};

const OUTCOME_COLOR = {
  success: { bg: "#d1fae5", fg: "#047857" },
  failure: { bg: "#fee2e2", fg: "#b91c1c" },
};

const labelize = (s) =>
  String(s || "")
    .split("_")
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");

const KV = ({ label, value, mono = false }) => (
  <Stack
    direction="row"
    sx={{
      px: 1.25,
      py: 0.75,
      borderBottom: "1px solid",
      borderColor: "divider",
      "&:last-child": { borderBottom: 0 },
    }}
    alignItems="flex-start"
    spacing={1}
  >
    <Typography
      sx={{
        flexShrink: 0,
        width: 110,
        color: "text.secondary",
        fontSize: "0.75rem",
        fontWeight: 500,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        flex: 1,
        fontSize: "0.75rem",
        fontFamily: mono ? "monospace" : undefined,
        wordBreak: "break-word",
      }}
    >
      {value ?? "—"}
    </Typography>
  </Stack>
);

const Section = ({ title, children }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography
      sx={{
        fontSize: "0.625rem",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        color: "text.secondary",
        mb: 0.75,
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const JsonBlock = ({ value }) => {
  if (value === undefined || value === null) {
    return (
      <Typography variant="caption" color="text.disabled">
        — none —
      </Typography>
    );
  }
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1.25,
        backgroundColor: "#0f172a",
        color: "#e2e8f0",
        borderRadius: "6px",
        overflowX: "auto",
        fontSize: "0.6875rem",
        fontFamily: "monospace",
        maxHeight: 260,
        lineHeight: 1.5,
      }}
    >
      {JSON.stringify(value, null, 2)}
    </Box>
  );
};

const ChangeRow = ({ change }) => (
  <Stack
    direction="row"
    spacing={1}
    sx={{
      px: 1.25,
      py: 0.75,
      borderBottom: "1px solid",
      borderColor: "divider",
      "&:last-child": { borderBottom: 0 },
    }}
  >
    <Typography
      sx={{
        flexShrink: 0,
        width: 140,
        color: "text.secondary",
        fontSize: "0.7rem",
        fontFamily: "monospace",
        wordBreak: "break-all",
      }}
    >
      {change.field}
    </Typography>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap" useFlexGap>
        <Chip
          label={JSON.stringify(change.before) ?? "—"}
          size="small"
          sx={{
            height: 18,
            fontSize: "0.625rem",
            backgroundColor: "#fee2e2",
            color: "#b91c1c",
            fontFamily: "monospace",
            maxWidth: "100%",
            "& .MuiChip-label": {
              px: 0.5,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
          }}
        />
        <Typography variant="caption" color="text.disabled">
          →
        </Typography>
        <Chip
          label={JSON.stringify(change.after) ?? "—"}
          size="small"
          sx={{
            height: 18,
            fontSize: "0.625rem",
            backgroundColor: "#d1fae5",
            color: "#047857",
            fontFamily: "monospace",
            maxWidth: "100%",
            "& .MuiChip-label": {
              px: 0.5,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
          }}
        />
      </Stack>
    </Box>
  </Stack>
);

export default function AuditLogDetailDrawer({ logId, onClose }) {
  const dispatch = useDispatch();
  const { current, loadingCurrent } = useSelector((s) => s.auditLog);

  useEffect(() => {
    if (logId) dispatch(fetchAuditLog(logId));
    else dispatch(clearCurrent());
  }, [logId, dispatch]);

  const open = Boolean(logId);
  const log = current && current._id === logId ? current : null;
  const sev = SEVERITY_COLOR[log?.severity] || SEVERITY_COLOR.info;
  const oc = OUTCOME_COLOR[log?.outcome] || OUTCOME_COLOR.success;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: (t) => t.zIndex.modal + 1 }}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 560, md: 680 },
          maxWidth: "100vw",
        },
      }}
    >
      {!log ? (
        <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
          {loadingCurrent ? (
            <CircularProgress size={20} />
          ) : (
            <Typography variant="caption" color="text.secondary">
              Entry not found.
            </Typography>
          )}
        </Box>
      ) : (
        <>
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 2,
              px: 2,
              pt: 2.5,
              pb: 2,
              background:
                "linear-gradient(135deg, #0d9488 0%, #14b8a6 50%, #2dd4bf 100%)",
              color: "common.white",
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                color: "common.white",
                backgroundColor: "rgba(255,255,255,0.15)",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.25)" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <Stack direction="row" alignItems="center" spacing={0.75} mb={0.75} flexWrap="wrap" useFlexGap>
              <Chip
                label={labelize(log.actionType)}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  backgroundColor: "rgba(255,255,255,0.22)",
                  color: "common.white",
                  border: "1px solid rgba(255,255,255,0.35)",
                }}
              />
              <Chip
                label={log.entityType}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.6875rem",
                  backgroundColor: "rgba(255,255,255,0.18)",
                  color: "common.white",
                }}
              />
              <Chip
                label={log.severity || "info"}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  backgroundColor: sev.bg,
                  color: sev.fg,
                }}
              />
              <Chip
                label={log.outcome || "success"}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  backgroundColor: oc.bg,
                  color: oc.fg,
                }}
              />
            </Stack>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "0.9375rem",
                letterSpacing: "-0.005em",
                wordBreak: "break-word",
                pr: 4,
              }}
            >
              {log.summary || "—"}
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", opacity: 0.92, mt: 0.25 }}>
              {dayjs(log.createdAt).format("DD MMM YYYY · HH:mm:ss")}
            </Typography>
          </Box>

          <Box sx={{ p: 1.5 }}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={6}>
                <Section title="Actor">
                  <Paper variant="outlined">
                    <KV label="Name" value={log.actor?.name || "—"} />
                    <KV label="Username" value={log.actor?.username || "—"} mono />
                    <KV label="Role" value={log.actor?.role || "—"} />
                    <KV label="ID" value={log.actor?.id || "—"} mono />
                  </Paper>
                </Section>
              </Grid>

              {(log.targetUser || log.device || log.company) && (
                <Grid item xs={12} md={6}>
                  <Section title="Target">
                    <Paper variant="outlined">
                      {log.company && (
                        <>
                          <KV label="Company" value={log.company.name || "—"} />
                          {log.company.manufacturing_id != null && (
                            <KV
                              label="Mfg ID"
                              value={`#${log.company.manufacturing_id}`}
                              mono
                            />
                          )}
                        </>
                      )}
                      {log.targetUser && (
                        <>
                          <KV
                            label="User"
                            value={
                              log.targetUser.name ||
                              log.targetUser.username ||
                              "—"
                            }
                          />
                          {log.targetUser.username && (
                            <KV
                              label="Username"
                              value={log.targetUser.username}
                              mono
                            />
                          )}
                        </>
                      )}
                      {log.device && (
                        <>
                          {log.device.name && (
                            <KV label="Device" value={log.device.name} />
                          )}
                          {log.device.imei && (
                            <KV label="IMEI" value={log.device.imei} mono />
                          )}
                        </>
                      )}
                      <KV label="Entity ID" value={log.entityId || "—"} mono />
                    </Paper>
                  </Section>
                </Grid>
              )}

              {log.changes?.length > 0 && (
                <Grid item xs={12}>
                  <Section title={`Changes (${log.changes.length})`}>
                    <Paper variant="outlined">
                      {log.changes.map((c, i) => (
                        <ChangeRow key={i} change={c} />
                      ))}
                    </Paper>
                  </Section>
                </Grid>
              )}

              {log.metadata && Object.keys(log.metadata).length > 0 && (
                <Grid item xs={12}>
                  <Section title="Metadata">
                    <JsonBlock value={log.metadata} />
                  </Section>
                </Grid>
              )}

              {log.before !== undefined && log.before !== null && (
                <Grid item xs={12}>
                  <Section title="Before (snapshot)">
                    <JsonBlock value={log.before} />
                  </Section>
                </Grid>
              )}

              {log.after !== undefined && log.after !== null && (
                <Grid item xs={12}>
                  <Section title="After (snapshot)">
                    <JsonBlock value={log.after} />
                  </Section>
                </Grid>
              )}

              {log.request && (
                <Grid item xs={12}>
                  <Section title="Request">
                    <Paper variant="outlined" sx={{ mb: 1 }}>
                      <KV label="Method" value={log.request.method || "—"} mono />
                      <KV label="Path" value={log.request.path || "—"} mono />
                      <KV label="IP" value={log.request.ip || "—"} mono />
                      {log.request.userAgent && (
                        <KV label="User-Agent" value={log.request.userAgent} />
                      )}
                    </Paper>
                    {log.request.params && Object.keys(log.request.params).length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>
                          params
                        </Typography>
                        <JsonBlock value={log.request.params} />
                      </Box>
                    )}
                    {log.request.query && Object.keys(log.request.query).length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>
                          query
                        </Typography>
                        <JsonBlock value={log.request.query} />
                      </Box>
                    )}
                    {log.request.body && Object.keys(log.request.body).length > 0 && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>
                          body
                        </Typography>
                        <JsonBlock value={log.request.body} />
                      </Box>
                    )}
                  </Section>
                </Grid>
              )}
            </Grid>
          </Box>
        </>
      )}
    </Drawer>
  );
}
